import { getLandmarkIndex } from '@/utils/landmarkDetection';

// Split out of AnnotationCanvasOverlay.tsx (react-refresh/only-export-components)
// — this is a plain data-transform helper, not a component; keeping it in a
// component file made that file export both a component and a function.
// Behavior unchanged.

/** Mirror map for lead_/trail_ resolution */
const FUNCTIONAL_BODY_PARTS: Record<string, { left: string; right: string }> = {
  shoulder: { left: 'left_shoulder', right: 'right_shoulder' },
  elbow:    { left: 'left_elbow',    right: 'right_elbow' },
  wrist:    { left: 'left_wrist',    right: 'right_wrist' },
  hip:      { left: 'left_hip',      right: 'right_hip' },
  knee:     { left: 'left_knee',     right: 'right_knee' },
  ankle:    { left: 'left_ankle',    right: 'right_ankle' },
  heel:     { left: 'left_heel',     right: 'right_heel' },
  foot_index: { left: 'left_foot_index', right: 'right_foot_index' },
  ear:      { left: 'left_ear',      right: 'right_ear' },
  eye:      { left: 'left_eye',      right: 'right_eye' },
  pinky:    { left: 'left_pinky',    right: 'right_pinky' },
  index:    { left: 'left_index',    right: 'right_index' },
  thumb:    { left: 'left_thumb',    right: 'right_thumb' },
};

/**
 * Resolve a functional landmark name (lead_hip, trail_knee, mid_shoulder, etc.)
 * to an anatomical name that getLandmarkIndex can handle.
 *
 * For mid_* names, returns the average position of left + right (handled separately).
 * For lead_* / trail_*, uses templateHandedness to determine which side is lead.
 *
 * Exported for unit tests only — do not call this function outside the canvas module.
 */
export function resolveFunctionalLandmarkName(
  name: string,
  leadSide: 'left' | 'right',
): string | [string, string] | null {
  // Already anatomical — pass through
  if (getLandmarkIndex(name) !== -1) return name;

  // mid_* → average of left + right
  if (name.startsWith('mid_')) {
    const bodyPart = name.slice(4); // e.g. "mid_hip" → "hip"
    const entry = FUNCTIONAL_BODY_PARTS[bodyPart];
    if (entry) return [entry.left, entry.right]; // caller must average
    return null;
  }

  // lead_* / trail_*
  const isLead = name.startsWith('lead_');
  const isTrail = name.startsWith('trail_');
  if (!isLead && !isTrail) return null;

  const bodyPart = name.replace(/^(lead_|trail_)/, '');
  const entry = FUNCTIONAL_BODY_PARTS[bodyPart];
  if (!entry) return null;

  // Lead side = the side specified by templateHandedness
  // Trail side = the opposite
  if (isLead) return leadSide === 'left' ? entry.left : entry.right;
  return leadSide === 'left' ? entry.right : entry.left;
}
